import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridToolbar,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  FraudMatchApplicant,
  getFraudMatches,
} from './riskHitSummaryApi';
import {
  mapApplicationTypeToRouteAppType,
  normalizeRouteAppType,
  parseRiskSummarySearchState,
  resolveCurrentApplicationContext,
} from './riskHitSummaryCurrentApplication';
import { RiskHitSummaryRow, buildRiskHitSummaryRows } from './riskHitSummaryUtils';
import { notifyPopupBlocked, openWithPopupCheck } from '../../utils/popupWindow';

type RiskHitSummaryContentProps = {
  applicant: FraudMatchApplicant;
  isActive?: boolean;
};

const DEFAULT_PAGE_SIZE = 15;
const ALL_PAGE_SIZE_VALUE = -1;
const DEFAULT_SORT = { field: 'applicationId', order: 'DESC' };
const RISK_SUMMARY_MIN_GRID_WIDTH = 980;
const RISK_SUMMARY_EXPORT_NAME = 'Risk Summary';
const RISK_SUMMARY_EXPORT_FIELDS = [
  'applicationId',
  'applicationType',
  'createdDate',
  'applicantsDisplay',
  'matchedFieldsDisplay',
  'riskLevel',
  'sectionTypesDisplay',
] as const;
const EMPTY_DISPLAY_VALUE = '\u2014';
const EMPTY_EXPORT_VALUE = '';

const normalizeExportValue = (value: unknown): string => {
  const normalized = String(value ?? '').trim();
  return normalized === EMPTY_DISPLAY_VALUE ? EMPTY_EXPORT_VALUE : normalized;
};

const resolveFormatterValue = (
  valueOrParams: unknown
): unknown => {
  if (
    valueOrParams &&
    typeof valueOrParams === 'object' &&
    'value' in (valueOrParams as Record<string, unknown>)
  ) {
    return (valueOrParams as { value?: unknown }).value;
  }

  return valueOrParams;
};

const normalizeSubscriberId = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
};
const resolveNavigationAppType = (
  applicationType: string,
  fallbackAppType?: string,
  explicitAppType?: string | null
): string => {
  const explicit = normalizeRouteAppType(explicitAppType);
  if (explicit) {
    return explicit;
  }

  const mapped = mapApplicationTypeToRouteAppType(applicationType);
  if (mapped) {
    return mapped;
  }

  const fallback = normalizeRouteAppType(fallbackAppType);
  if (fallback) {
    return fallback;
  }

  return '1';
};

const mapRouteAppTypeToLabel = (appType: string, applicationType: string): string => {
  if (appType === '2') {
    return 'Doc Upload';
  }

  if (appType === '3') {
    return 'Guest Card';
  }

  if (appType === '1') {
    return 'Application';
  }

  const normalizedType = String(applicationType || '').trim().toUpperCase();
  return normalizedType === 'VR' ? 'Doc Upload' : 'Application';
};

const buildApplicationEditPath = (
  pathname: string,
  subscriberId: string,
  applicationId: string,
  appType: string
) => {
  const normalizedPathname = String(pathname || '').toLowerCase();
  const isLandlordPath = normalizedPathname.includes('/landloard/');

  if (isLandlordPath) {
    return `/landloard/landLoardInbox/View/${subscriberId}/edit/${applicationId}/${appType}`;
  }

  return `/Ats/Subscriber/Inbox/${subscriberId}/edit/${applicationId}/${appType}`;
};

const pickStringValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const toBooleanOrUndefined = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }

  return undefined;
};

type NavigationOrigin = {
  pathname: string;
  search: string;
  hash: string;
};

const normalizeNavigationOrigin = (value: unknown): NavigationOrigin | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const pathname = pickStringValue((value as { pathname?: unknown }).pathname);
  if (!pathname) {
    return null;
  }

  return {
    pathname,
    search: pickStringValue((value as { search?: unknown }).search) || '',
    hash: pickStringValue((value as { hash?: unknown }).hash) || '',
  };
};

const normalizeIdentityToken = (value: unknown): string => String(value ?? '').trim();

const composeParticipantDisplayName = (
  participant: Record<string, unknown>
): string | null => {
  const fullName = pickStringValue(participant.fullName);
  if (fullName) {
    return fullName;
  }

  const firstName = pickStringValue(participant.firstName);
  const middleName = pickStringValue(participant.middleName);
  const lastName = pickStringValue(participant.lastName);
  const composite = [firstName, middleName, lastName].filter(Boolean).join(' ');
  return composite || null;
};

const uniqNonEmptyNames = (names: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(
      names.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
    )
  );

const stripCurrentApplicantFromCurrentApplicationDisplay = (
  row: RiskHitSummaryRow,
  currentApplicationId: string,
  currentApplicantId: string
): RiskHitSummaryRow => {
  if (!currentApplicationId || !currentApplicantId) {
    return row;
  }

  if (normalizeIdentityToken(row.applicationId) !== currentApplicationId) {
    return row;
  }

  const participants = Array.isArray(row.sourceApplication?.participants)
    ? row.sourceApplication.participants
    : [];
  const participantNames = uniqNonEmptyNames(
    participants.flatMap((participant) => {
      const participantId = normalizeIdentityToken(participant?.applicantId);
      if (!participantId || participantId === currentApplicantId) {
        return [];
      }

      const displayName = composeParticipantDisplayName(participant as Record<string, unknown>);
      return displayName ? [displayName] : [];
    })
  );
  if (participantNames.length > 0) {
    return {
      ...row,
      applicantsDisplay: participantNames.join(', '),
    };
  }

  const matchedApplicants = Array.isArray(row.sourceApplication?.matchedApplicants)
    ? row.sourceApplication.matchedApplicants
    : [];
  const fallbackNames = uniqNonEmptyNames(
    matchedApplicants.flatMap((matchedApplicant) => {
      const matchedApplicantId = normalizeIdentityToken(matchedApplicant?.applicantId);
      if (!matchedApplicantId || matchedApplicantId === currentApplicantId) {
        return [];
      }

      const fullName = matchedApplicant?.matchedValues?.fullName;
      return typeof fullName === 'string' && fullName.trim().length > 0 ? [fullName] : [];
    })
  );
  if (fallbackNames.length > 0) {
    return {
      ...row,
      applicantsDisplay: fallbackNames.join(', '),
    };
  }

  return row;
};

const RiskHitSummaryContent = ({
  applicant,
  isActive = true,
}: RiskHitSummaryContentProps) => {
  const theme = useTheme();
  const notify = useNotify();
  const isCompactTable = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const {
    id: routeSubscriberId,
    appType: routeAppType,
    applicationId: routeApplicationId,
  } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<RiskHitSummaryRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const hasRows = rows.length > 0;
  const requestedQueryKeyRef = useRef<string | null>(null);

  const applicantPayload = useMemo<FraudMatchApplicant>(
    () => ({
      applicantId: applicant?.applicantId,
      firstName: applicant?.firstName,
      lastName: applicant?.lastName,
      email: applicant?.email,
      phone: applicant?.phone,
      ssn: applicant?.ssn,
      idDocumentNumber: applicant?.idDocumentNumber,
    }),
    [
      applicant?.applicantId,
      applicant?.firstName,
      applicant?.lastName,
      applicant?.email,
      applicant?.phone,
      applicant?.ssn,
      applicant?.idDocumentNumber,
    ]
  );

  const applicantRequestKey = useMemo(
    () =>
      JSON.stringify([
        applicantPayload.firstName || '',
        applicantPayload.lastName || '',
        applicantPayload.email || '',
        applicantPayload.phone || '',
        applicantPayload.ssn || '',
        applicantPayload.idDocumentNumber || '',
      ]),
    [applicantPayload]
  );

  const currentApplicationContext = useMemo(
    () =>
      resolveCurrentApplicationContext({
        routeApplicationId,
        routeAppType,
        locationState: location.state as Record<string, unknown> | null,
        locationSearch: location.search,
      }),
    [routeApplicationId, routeAppType, location.state, location.search]
  );
  const navigationOrigin = useMemo(
    () =>
      normalizeNavigationOrigin(
        (location.state as { from?: unknown } | null)?.from
      ) ||
      normalizeNavigationOrigin(currentApplicationContext.searchState?.from) || {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
    [
      currentApplicationContext.searchState,
      location.hash,
      location.pathname,
      location.search,
      location.state,
    ]
  );

  const subscriberId = useMemo(() => {
    const locationSubscriberId = (location.state as { SubscriberID?: string } | null)
      ?.SubscriberID;
    const resolvedSubscriberId = routeSubscriberId || locationSubscriberId || '';
    return String(resolvedSubscriberId);
  }, [location.state, routeSubscriberId]);
  const queryKey = useMemo(
    () =>
      JSON.stringify({
        applicantRequestKey,
        currentApplicationId: currentApplicationContext.currentApplicationId || '',
        currentRouteAppType: currentApplicationContext.currentRouteAppType || '',
        subscriberId: subscriberId || '',
      }),
    [
      applicantRequestKey,
      currentApplicationContext,
      subscriberId,
    ]
  );

  const loadFraudMatches = useCallback(async (forceReload = false) => {
    if (!forceReload && requestedQueryKeyRef.current === queryKey) {
      return;
    }

    requestedQueryKeyRef.current = queryKey;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const currentApplicationId = currentApplicationContext.currentApplicationId;
      const currentApplicantId = String(applicant?.applicantId || '').trim();
      const response = await getFraudMatches(applicantPayload, {
        context: {
          applicationId: currentApplicationId || undefined,
          applicationType: currentApplicationContext.currentRouteAppType
            ? mapRouteAppTypeToLabel(currentApplicationContext.currentRouteAppType, '')
            : undefined,
          applicantId: currentApplicantId || undefined,
          subscriberId: subscriberId || undefined,
        },
      });

      const normalizedCurrentApplicationId = normalizeIdentityToken(currentApplicationId);
      const nextRows = buildRiskHitSummaryRows(response.matchedApplications).map((row) =>
        stripCurrentApplicantFromCurrentApplicationDisplay(
          row,
          normalizedCurrentApplicationId,
          currentApplicantId
        )
      );
      setRows(nextRows);
    } catch (_error) {
      setErrorMessage('Unable to load risk summary. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    applicantPayload,
    currentApplicationContext,
    applicant?.applicantId,
    queryKey,
    subscriberId,
  ]);

  const sortModel = useMemo<GridSortModel>(
    () => [{ field: sort.field, sort: sort.order.toLowerCase() as 'asc' | 'desc' }],
    [sort]
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page: Math.max(page - 1, 0), pageSize }),
    [page, pageSize]
  );

  const handleSortModelChange = useCallback((nextSortModel: GridSortModel) => {
    const nextSort = nextSortModel[0];
    if (!nextSort?.field) {
      return;
    }
    const normalizedOrder =
      nextSort.sort === 'asc' ? 'ASC' : nextSort.sort === 'desc' ? 'DESC' : 'ASC';
    setSort((prev) =>
      prev.field === nextSort.field && prev.order === normalizedOrder
        ? prev
        : {
            field: nextSort.field,
            order: normalizedOrder,
          }
    );
    setPage(1);
  }, []);

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      const nextPageSize = nextPaginationModel.pageSize;
      if (nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
        setPage(1);
        return;
      }

      const nextPage = nextPaginationModel.page + 1;
      setPage((currentPage) => (currentPage === nextPage ? currentPage : nextPage));
    },
    [pageSize]
  );

  useEffect(() => {
    if (!isActive) {
      requestedQueryKeyRef.current = null;
      return;
    }

    void loadFraudMatches();
  }, [isActive, queryKey, loadFraudMatches]);

  const shouldShowInitialLoading = isLoading && !hasRows;
  const shouldShowBlockingError = Boolean(errorMessage) && !hasRows;
  const shouldShowEmptyState = !isLoading && !errorMessage && !hasRows;
  const shouldShowGrid = hasRows;
  const shouldShowReloadingIndicator = isLoading && hasRows;
  const shouldShowStaleDataError = Boolean(errorMessage) && hasRows;
  const compareApplicationIdValues = useCallback(
    (valueA: string | number, valueB: string | number) => {
      const numericA = Number(valueA);
      const numericB = Number(valueB);
      if (Number.isFinite(numericA) && Number.isFinite(numericB)) {
        return numericA - numericB;
      }
      return String(valueA ?? '').localeCompare(String(valueB ?? ''));
    },
    []
  );

  const columns = useMemo<GridColDef<RiskHitSummaryRow>[]>(
    () => [
      {
        field: 'applicationId',
        headerName: 'ID',
        width: 95,
        minWidth: 85,
        sortComparator: compareApplicationIdValues,
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
        renderCell: (params) => {
          const applicationId = String(params.row.applicationId || '');
          if (!applicationId) {
            return applicationId || '\u2014';
          }

          return (
            <Button
              variant='text'
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                const navigationSource = (params.row.sourceApplication || {}) as Record<
                  string,
                  unknown
                >;

                const sourceSubscriberId =
                  normalizeSubscriberId(navigationSource.SubscriberID) ||
                  normalizeSubscriberId(navigationSource.subscriberID) ||
                  normalizeSubscriberId(navigationSource.subscriberId) ||
                  normalizeSubscriberId(navigationSource.subscriberid) ||
                  normalizeSubscriberId(params.row.navigationSubscriberId);
                const nextSubscriberId =
                  sourceSubscriberId || normalizeSubscriberId(subscriberId);
                if (!nextSubscriberId) {
                  return;
                }

                const sourceAppTypeHint =
                  normalizeRouteAppType(navigationSource.app_invitation_type) ||
                  normalizeRouteAppType(navigationSource.appInvitationType) ||
                  normalizeRouteAppType(navigationSource.appType) ||
                  normalizeRouteAppType(navigationSource.applicationTypeId) ||
                  normalizeRouteAppType(params.row.navigationAppType);
                const sourceApplicationType =
                  pickStringValue(navigationSource.ApplicationType) ||
                  pickStringValue(navigationSource.applicationType) ||
                  params.row.applicationType;
                const nextAppType = resolveNavigationAppType(
                  sourceApplicationType,
                  routeAppType,
                  sourceAppTypeHint
                );
                const targetPath = buildApplicationEditPath(
                  location.pathname,
                  nextSubscriberId,
                  applicationId,
                  nextAppType
                );
                const routeApplicationTypeLabel = mapRouteAppTypeToLabel(
                  nextAppType,
                  sourceApplicationType
                );
                const sourceApplicationStatus =
                  pickStringValue(navigationSource.ApplicationStatus) ||
                  pickStringValue(navigationSource.applicationStatus);
                const sourceScreeningDetails =
                  pickStringValue(navigationSource.ScreeningDetails) ||
                  pickStringValue(navigationSource.screeningDetails);
                const sourceArchived =
                  toBooleanOrUndefined(navigationSource.Archived) ??
                  toBooleanOrUndefined(navigationSource.archived);
                const sourceIsRevamp =
                  toBooleanOrUndefined(navigationSource.IsRevamp) ??
                  toBooleanOrUndefined(navigationSource.isRevamp);
                const sourceIsAppCreatedUsingNet =
                  toBooleanOrUndefined(navigationSource.isAppCreatedUsingNet) ??
                  (typeof sourceIsRevamp === 'boolean'
                    ? !sourceIsRevamp
                    : undefined);
                const sourceApplicantId =
                  pickStringValue(navigationSource.ApplicantID) ||
                  pickStringValue(navigationSource.applicantId);
                const sourceApplicantList =
                  pickStringValue(navigationSource.ApplicantList) ||
                  pickStringValue(navigationSource.applicantsDisplay);
                const sourceAppDate =
                  pickStringValue(navigationSource.AppDate) ||
                  pickStringValue(navigationSource.applicationCreatedOn) ||
                  pickStringValue(navigationSource.createdAt);
                const statePassthroughKeys = [
                  'ROWNUM',
                  'ROWID',
                  'AppDate',
                  'ApplicantList',
                  'Property',
                  'Agent',
                  'AppliedForAddress',
                  'AppliedForRent',
                  'ApplicantCount',
                  'Subscribertype',
                  'SubscriberName',
                  'EmailStatus',
                  'SecondaryApp',
                  'State',
                  'RI',
                  'Score',
                ] as const;
                const passthroughState = statePassthroughKeys.reduce<
                  Record<string, unknown>
                >((acc, key) => {
                  if (navigationSource[key] !== undefined) {
                    acc[key] = navigationSource[key];
                  }
                  return acc;
                }, {});
                const nextState = {
                  ...passthroughState,
                  id: applicationId,
                  appid: applicationId,
                  SubscriberID: nextSubscriberId,
                  subscriberid: nextSubscriberId,
                  app_invitation_type: Number(nextAppType),
                  ApplicationType: routeApplicationTypeLabel,
                  ...(sourceApplicationStatus
                    ? { ApplicationStatus: sourceApplicationStatus }
                    : {}),
                  ...(sourceScreeningDetails
                    ? { ScreeningDetails: sourceScreeningDetails }
                    : {}),
                  ...(typeof sourceArchived === 'boolean'
                    ? { Archived: sourceArchived }
                    : {}),
                  ...(typeof sourceIsRevamp === 'boolean'
                    ? { IsRevamp: sourceIsRevamp }
                    : {}),
                  ...(typeof sourceIsAppCreatedUsingNet === 'boolean'
                    ? { isAppCreatedUsingNet: sourceIsAppCreatedUsingNet }
                    : {}),
                  ...(sourceApplicantId ? { ApplicantID: sourceApplicantId } : {}),
                  ...(sourceApplicantList ? { ApplicantList: sourceApplicantList } : {}),
                  ...(sourceAppDate ? { AppDate: sourceAppDate } : {}),
                  from: navigationOrigin,
                };
                const targetUrl = `${targetPath}?data=${encodeURIComponent(
                  JSON.stringify(nextState)
                )}`;
                openWithPopupCheck(targetUrl, '_blank', 'noopener,noreferrer', {
                  onBlocked: notifyPopupBlocked(notify),
                });
              }}
              sx={{
                minWidth: 'auto',
                px: 0,
                textTransform: 'none',
                justifyContent: 'flex-start',
                fontWeight: 700,
              }}
            >
              {applicationId}
            </Button>
          );
        },
      },
      {
        field: 'applicationType',
        headerName: 'Type',
        width: 90,
        minWidth: 85,
        renderCell: (params) => params.row.applicationType || EMPTY_DISPLAY_VALUE,
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 130,
        minWidth: 120,
        renderCell: (params) => params.row.createdDate || '\u2014',
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
      },
      {
        field: 'applicantsDisplay',
        headerName: 'Applicants',
        width: 150,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => params.row.applicantsDisplay || '\u2014',
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
      },
      {
        field: 'matchedFieldsDisplay',
        headerName: 'Matched On',
        width: 150,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => params.row.matchedFieldsDisplay || EMPTY_DISPLAY_VALUE,
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
      },
      {
        field: 'riskLevel',
        headerName: 'Unverifiable Sections?',
        width: 175,
        minWidth: 165,
        renderCell: (params) => {
          const value = String(params.row.riskLevel || 'No');
          const isYes = value === 'Yes';
          if (!isYes) return EMPTY_DISPLAY_VALUE
          return (
            <Chip
              label={value}
              size='small'
              sx={{
                minWidth: 64,
                color: '#fff',
                fontWeight: 700,
                bgcolor: isYes ? 'error.main' : 'success.main',
              }}
            />
          );
        },
        valueFormatter: (valueOrParams) =>
          String(resolveFormatterValue(valueOrParams) || 'No') === 'Yes'
            ? 'Yes'
            : 'No',
      },
      {
        field: 'sectionTypesDisplay',
        headerName: 'Sections',
        width: 150,
        minWidth: 140,
        sortable: false,
        renderCell: (params) =>
          params.row.sectionTypes.length ? params.row.sectionTypes.join(', ') : EMPTY_DISPLAY_VALUE,
        valueFormatter: (valueOrParams) =>
          normalizeExportValue(resolveFormatterValue(valueOrParams)),
      },
    ],
    [
      compareApplicationIdValues,
      location,
      routeAppType,
      subscriberId,
    ]
  );

  const compactColumns = useMemo<GridColDef<RiskHitSummaryRow>[]>(
    () => [
      {
        field: 'summaryColumn',
        headerName: 'Summary',
        sortable: false,
        filterable: false,
        minWidth: 0,
        flex: 1,
        renderCell: (params) => (
          <Stack spacing={0.5} sx={{ py: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              ID: {params.row.applicationId || EMPTY_DISPLAY_VALUE}
            </Typography>
            <Typography variant='body2'>
              Type: {params.row.applicationType || EMPTY_DISPLAY_VALUE}
            </Typography>
            <Typography variant='body2'>
              Created: {params.row.createdDate || EMPTY_DISPLAY_VALUE}
            </Typography>
            <Typography variant='body2'>
              Applicants: {params.row.applicantsDisplay || EMPTY_DISPLAY_VALUE}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'detailsColumn',
        headerName: 'Details',
        sortable: false,
        filterable: false,
        minWidth: 0,
        flex: 1,
        renderCell: (params) => (
          <Stack spacing={0.5} sx={{ py: 1 }}>
            <Typography variant='body2'>
              Matched On:{' '}
              {params.row.matchedFields.length
                ? params.row.matchedFields.join(', ')
                : EMPTY_DISPLAY_VALUE}
            </Typography>
            <Typography variant='body2'>
              Unverifiable Sections?:{' '}
              {String(params.row.riskLevel || 'No') === 'Yes' ? 'Yes' : EMPTY_DISPLAY_VALUE}
            </Typography>
            <Typography variant='body2'>
              Sections:{' '}
              {params.row.sectionTypes.length
                ? params.row.sectionTypes.join(', ')
                : EMPTY_DISPLAY_VALUE}
            </Typography>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <>
      {shouldShowInitialLoading && (
        <Box
          sx={{
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography>Loading risk summary...</Typography>
        </Box>
      )}

      {shouldShowBlockingError && (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={() => loadFraudMatches(true)}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {shouldShowEmptyState && <Typography>No matches found</Typography>}

      {shouldShowReloadingIndicator && (
        <Box sx={{ mb: 1 }}>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            Updating risk summary...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {shouldShowStaleDataError && (
        <Alert
          severity='warning'
          sx={{ mb: 1 }}
          action={
            <Button color='inherit' size='small' onClick={() => loadFraudMatches(true)}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {shouldShowGrid && (
        <Box
          sx={{
            width: '100%',
            height: 'auto',
            overflowX: 'auto',
          }}
        >
          <DataGridPro
            autoHeight
            rows={rows}
            columns={isCompactTable ? compactColumns : columns}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: RISK_SUMMARY_EXPORT_NAME,
                  fields: [...RISK_SUMMARY_EXPORT_FIELDS],
                  utf8WithBom: true,
                },
                printOptions: {
                  disableToolbarButton: true,
                },
              },
            }}
            getRowId={(row) => row.id}
            loading={shouldShowInitialLoading}
            sortingOrder={['asc', 'desc']}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            getRowHeight={() => 'auto'}
            pageSizeOptions={[2, 15, 25, 50, { value: ALL_PAGE_SIZE_VALUE, label: 'All' }]}
            disableRowSelectionOnClick
            disableColumnFilter
            disableDensitySelector
            disableVirtualization
            sx={{
              minWidth: isCompactTable ? '100%' : RISK_SUMMARY_MIN_GRID_WIDTH,
              '& .MuiDataGrid-columnHeaders': {
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                alignItems: isCompactTable ? 'flex-start' : 'center',
                whiteSpace: isCompactTable ? 'normal' : 'nowrap',
                lineHeight: isCompactTable ? 1.4 : 'normal',
                py: 0.5,
              },
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'auto !important',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#F9F9F9',
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default RiskHitSummaryContent;

